import { Context as FeatureToggleContext } from 'application/FeatureToggle';
import { clearReloadReason, getReloadReason } from 'application/wt-debugger';
import { Amplify } from 'aws-amplify';
import React, { useContext, useEffect, useState } from 'react';
import Error from 'styleguide/Error';
import Loading from 'styleguide/Loading';
import PublicLayout from 'styleguide/layout/Public';

export interface IConfiguration {
  AWSRegion?: string;
  AWSUploadsBucket?: string;
  DeploymentEnvironment?: string;
  IdentityPoolId?: string;
  UserPoolId?: string;
  UserPoolWebClientId?: string;
  IdleScreenLockDelay?: number;
  IdleLogoutDelay?: number;
  FlexLicenseKey?: string;
  GoogleAnalyticsMeasureId?: string;
  OauthURL?: string;
  OauthClientID?: string;
  OauthRedirectURL?: string;
  GooleMapsApiKey?: string;
  LaunchDarklyClientSideId?: string;
  ClinicianCbrReportId?: string;
  ClinicianCbrTenantId?: string;
}

const defaultValue: IConfiguration = {};

export const Context = React.createContext(defaultValue);

// Set the global S3 client configuration
(global as any).AWS = {
  config: {
    s3: {
      maxRetries: 0,
      httpOptions: {
        timeout: 120000, // 2 minute timeout for response
      },
    },
  },
};
const Configuration: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [configuration, setConfiguration] = useState<
    IConfiguration | undefined
  >();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>();
  const { EnableWoundtechADIntegration } = useContext(FeatureToggleContext);

  useEffect(() => {
    fetch(window?._env_?.CONFIGURATION_URI || '/api/config')
      .then(async (response) => {
        const data = await response.json();
        setConfiguration(data);
        setLoading(false);
      })
      .catch((error) => {
        // biome-ignore lint/suspicious/noConsole: reason here
        console.warn(
          'Failed to fetch configuration from API, using local config:',
          error
        );
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <PublicLayout>
        <Error
          text={`Could not load the application configuration. ${error?.message}.`}
        />
      </PublicLayout>
    );
  }

  if (!configuration) {
    return null;
  }

  const configurations = () => {
    const baseConfig = {
      Auth: {
        region: configuration?.AWSRegion,
        userPoolId: configuration?.UserPoolId,
        userPoolWebClientId: configuration?.UserPoolWebClientId,
        authenticationFlowType: 'USER_PASSWORD_AUTH',
        identityPoolId: configuration?.IdentityPoolId,
        storage: window?.sessionStorage,
      },
      Storage: {
        AWSS3: {
          bucket: configuration?.AWSUploadsBucket,
          region: configuration?.AWSRegion,
          defaultAccessLevel: 'private',
        },
      },
    };

    if (EnableWoundtechADIntegration) {
      return {
        ...baseConfig,
        Auth: {
          ...baseConfig.Auth,
          oauth: {
            domain: window?._env_?.OAUTH_DOMAIN,
            scope: ['email', 'openid'],
            label: 'Woundtech Login',
            redirectSignIn: window?._env_?.REDIRECT_SIGN_IN,
            redirectSignOut: window?._env_?.REDIRECT_SIGN_OUT,
            responseType: 'code',
          },
        },
      };
    }

    return baseConfig;
  };

  const reloadReason = getReloadReason();
  if (reloadReason) {
    clearReloadReason();
  }

  const config = configurations();

  Amplify.configure(config);
  return <Context.Provider value={configuration}>{children}</Context.Provider>;
};

export default Configuration;
